import { useState } from 'react'
import { MapPin, Phone, User, Package, Truck, Clock } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { OrderStatusBadge } from '../../components/OrderStatusBadge'
import { PAY_TYPE_LABELS, ORDER_STATUS, ORDER_CHANGE_TYPE_LABELS, type Order, type DeliveryParams, type SplitDeliveryParams, type OrderStatus } from '@/schemas/supplier'
import { DeliveryDialog } from './DeliveryDialog'

interface OrderDetailDrawerProps {
  order: Order | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onDelivery?: (orderId: number, data: DeliveryParams | SplitDeliveryParams) => void
  isDeliveryPending?: boolean
  orderStatusList?: OrderStatus[]
  isStatusLoading?: boolean
}

export function OrderDetailDrawer({
  order,
  open,
  onOpenChange,
  onDelivery,
  isDeliveryPending,
  orderStatusList,
  isStatusLoading,
}: OrderDetailDrawerProps) {
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false)

  // 是否可以发货
  const canDeliver = order && order.status === ORDER_STATUS.PENDING_SHIPMENT

  // 处理发货
  const handleDelivery = (data: DeliveryParams | SplitDeliveryParams) => {
    if (onDelivery && order) {
      onDelivery(order.id, data)
      setShowDeliveryDialog(false)
    }
  }

  // 获取状态类型标签
  const getChangeTypeLabel = (type: string) => {
    return ORDER_CHANGE_TYPE_LABELS[type] || type
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>订单详情</SheetTitle>
            {order && (
              <SheetDescription>订单号：{order.order_id}</SheetDescription>
            )}
          </SheetHeader>

          {!order ? (
            <div className="space-y-6 py-6">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          ) : (
            <>
              {/* 操作按钮 */}
              {canDeliver && (
                <div className="py-4">
                  <Button className="w-full" onClick={() => setShowDeliveryDialog(true)}>
                    <Truck className="mr-1.5 h-4 w-4" />
                    发货
                  </Button>
                </div>
              )}

              {/* 状态概览 */}
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">订单状态</span>
                <OrderStatusBadge status={order.status} />
              </div>

              {/* Tabs 区域 */}
              <Tabs defaultValue="order" className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="order">订单信息</TabsTrigger>
                  <TabsTrigger value="products">商品信息</TabsTrigger>
                  <TabsTrigger value="records">订单记录</TabsTrigger>
                  <TabsTrigger value="delivery">发货记录</TabsTrigger>
                </TabsList>

                <ScrollArea className="h-[calc(100vh-320px)]">
                  {/* 订单信息 Tab */}
                  <TabsContent value="order" className="space-y-4 mt-4">
                    {/* 基本信息 */}
                    <div className="rounded-lg border bg-card p-4">
                      <h3 className="font-medium mb-3">基本信息</h3>
                      <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-muted-foreground">订单号：</span>
                            <span className="font-mono">{order.order_id}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">支付方式：</span>
                            <span>{PAY_TYPE_LABELS[order.paid] || '未知'}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-muted-foreground">下单时间：</span>
                            <span>{order.add_time}</span>
                          </div>
                          {order.paid_time && (
                            <div>
                              <span className="text-muted-foreground">支付时间：</span>
                              <span>{order.paid_time}</span>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-muted-foreground">商品数量：</span>
                            <span>{order.total_num} 件</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">订单金额：</span>
                            <span className="font-medium">¥{(order.total_price ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between font-medium text-base pt-2 border-t">
                          <span>实付金额：</span>
                          <span className="text-primary text-lg">¥{(order.pay_price ?? 0).toFixed(2)}</span>
                        </div>
                        {order.mark && (
                          <div>
                            <span className="text-muted-foreground">买家备注：</span>
                            <span>{order.mark}</span>
                          </div>
                        )}
                        {order.remark && (
                          <div>
                            <span className="text-muted-foreground">商家备注：</span>
                            <span>{order.remark}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 收货人信息 */}
                    <div className="rounded-lg border bg-card p-4">
                      <h3 className="font-medium flex items-center gap-2 mb-3">
                        <MapPin className="h-4 w-4" />
                        收货信息
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{order.real_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{order.user_phone}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <span>{order.user_address}</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* 商品信息 Tab */}
                  <TabsContent value="products" className="mt-4">
                    <div className="rounded-lg border bg-card p-4">
                      <h3 className="font-medium flex items-center gap-2 mb-4">
                        <Package className="h-4 w-4" />
                        商品列表
                      </h3>
                      <div className="space-y-4">
                        {order.cart_info?.map((item, index) => (
                          <div key={item.cart_id}>
                            {index > 0 && <Separator className="mb-4" />}
                            <div className="flex items-start gap-3">
                              <img
                                src={item.product_image}
                                alt={item.product_name}
                                className="w-16 h-16 object-cover rounded border"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{item.product_name}</p>
                                {item.product_attr && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{item.product_attr}</p>
                                )}
                                {item.sku && (
                                  <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>
                                )}
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-sm">
                                    ¥{(item.price ?? 0).toFixed(2)}
                                    {typeof item.pay_price === 'number' && item.pay_price !== item.total_price && (
                                      <span className="text-xs text-muted-foreground ml-1">
                                        （实付 ¥{item.pay_price.toFixed(2)}）
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-sm text-muted-foreground">x{item.cart_num ?? 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Separator className="my-4" />
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">商品总数：</span>
                          <span>{order.total_num} 件</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">商品总价：</span>
                          <span>¥{(order.total_price ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-base pt-2 border-t">
                          <span>实付金额：</span>
                          <span className="text-primary">¥{(order.pay_price ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* 订单记录 Tab */}
                  <TabsContent value="records" className="mt-4">
                    <div className="rounded-lg border bg-card p-4">
                      <h3 className="font-medium flex items-center gap-2 mb-4">
                        <Clock className="h-4 w-4" />
                        订单记录
                      </h3>
                      {isStatusLoading ? (
                        <div className="space-y-4">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="flex gap-3">
                              <Skeleton className="h-2 w-2 rounded-full mt-2" />
                              <div className="flex-1 space-y-2">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-3 w-40" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : orderStatusList && orderStatusList.length > 0 ? (
                        <div className="relative">
                          <div className="absolute left-1.5 top-2 bottom-2 w-px bg-border" />
                          <div className="space-y-4">
                            {orderStatusList.map((status) => (
                              <div key={status.id} className="flex gap-3 relative">
                                <div className="w-3 h-3 rounded-full bg-primary mt-1 z-10" />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">
                                      {getChangeTypeLabel(status.change_type)}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {status.change_time}
                                    </span>
                                  </div>
                                  {status.change_message && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {status.change_message}
                                    </p>
                                  )}
                                  {status.change_manager && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      操作人：{status.change_manager}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          暂无订单记录
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* 发货记录 Tab */}
                  <TabsContent value="delivery" className="mt-4">
                    <div className="rounded-lg border bg-card p-4">
                      <h3 className="font-medium flex items-center gap-2 mb-4">
                        <Truck className="h-4 w-4" />
                        物流信息
                      </h3>
                      {order.delivery_name ? (
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">快递公司：</span>
                            <span>{order.delivery_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">快递单号：</span>
                            <span className="font-mono">{order.delivery_id}</span>
                          </div>
                          {order.delivery_code && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">快递编码：</span>
                              <span className="font-mono">{order.delivery_code}</span>
                            </div>
                          )}
                          {order.delivery_time && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">发货时间：</span>
                              <span>{order.delivery_time}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          暂未发货
                        </div>
                      )}
                    </div>

                    {/* 发货历史 */}
                    {orderStatusList && orderStatusList.filter(s => s.change_type === 'delivery').length > 0 && (
                      <div className="rounded-lg border bg-card p-4 mt-4">
                        <h3 className="font-medium mb-4">发货历史</h3>
                        <div className="space-y-3">
                          {orderStatusList
                            .filter(s => s.change_type === 'delivery')
                            .map((status) => (
                              <div key={status.id} className="text-sm p-3 bg-muted/50 rounded">
                                <div className="flex justify-between">
                                  <span>发货</span>
                                  <span className="text-muted-foreground">{status.change_time}</span>
                                </div>
                                {status.change_message && (
                                  <p className="text-muted-foreground mt-1">{status.change_message}</p>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* 发货对话框 */}
      {order && (
        <DeliveryDialog
          order={order}
          open={showDeliveryDialog}
          onOpenChange={setShowDeliveryDialog}
          onConfirm={handleDelivery}
          isLoading={isDeliveryPending}
        />
      )}
    </>
  )
}
